import React from 'react';

/**
 * CoverageTag (Aurora Cockpit) — the coverage / provenance badge of the
 * round-2 design system (`.da-tag`; see src/styles/auroraCockpit.css):
 *   verified → "V verified" (green outline)
 *   reported → "R reported" (amber outline)
 *   none     → "— no data"  (muted)
 *
 * Token-driven mono micro-label. Use for per-field coverage on SED / evidence
 * datasheets. For count chips use `.da-chip` (the Chip below).
 */
export type CoverageLevel = 'verified' | 'reported' | 'none';

const LEVEL_MODIFIER: Record<CoverageLevel, string> = {
  verified: 'da-tag--v',
  reported: 'da-tag--r',
  none: '',
};

const DEFAULT_LABEL: Record<CoverageLevel, string> = {
  verified: 'V verified',
  reported: 'R reported',
  none: '— no data',
};

export interface CoverageTagProps {
  level: CoverageLevel;
  /** Override the default label text. */
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const CoverageTag: React.FC<CoverageTagProps> = ({
  level,
  children,
  className = '',
  'data-testid': testId,
}) => (
  <span
    className={`da-tag ${LEVEL_MODIFIER[level]} ${className}`.trim()}
    data-testid={testId}
  >
    {children ?? DEFAULT_LABEL[level]}
  </span>
);

/**
 * Chip — the neutral count / label chip (`.da-chip`). Mono, tabular, hairline.
 * Provided here so surfaces have a typed wrapper for the round-2 chip without a
 * second file; pairs with CoverageTag.
 */
export interface ChipProps {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const Chip: React.FC<ChipProps> = ({ children, className = '', 'data-testid': testId }) => (
  <span className={`da-chip ${className}`.trim()} data-testid={testId}>
    {children}
  </span>
);
